import {
  Checkbox,
  FormControlLabel,
  Grid,
  SelectChangeEvent,
  useEventCallback,
} from '@mui/material';
import { Fragment, memo, useMemo, useState } from 'react';
import { AccordionGeneralityFormVM } from 'front-office/view-models/certificate/generality-form';
import { DatePickerView } from '@mui/lab/DatePicker/shared';
import ChooseDate from 'shell/components/shared/choice-date';
import { PmiButton } from 'shell/components/shared/button';
import { DateTime } from 'luxon';
import ItalianForeignState from './italian-foreign-state';
import Document from './document';
import { noop } from 'shell/utils/noop';
import { useHttpFetch } from 'shell/hooks/use-http-fetch';
import { __PERSON_API_URL__ } from 'shell/constants/environment';
import { HttpResponse } from 'shell/models/http-response';
import { transformerPersonGeneralityList } from './transformer-anpr';
import { CertificateFormVM } from 'front-office/view-models/certificate/certificate-form';
import { transformSearchPersonDataset } from 'shell/utils/transform-search-person';
import { AccordionApplicantFormVM } from 'front-office/view-models/certificate/applicant-form';
import { ResponsePromise } from 'shell/hooks/use-http-fetch/stateless/type';
import { HeadCell } from 'shell/components/shared/table/types';
import { PersonVM } from 'shell/view-models/search-person';
import { FormikErrors, FormikTouched } from 'formik';
import PmiTextField from 'shell/components/shared/text-field';
import InputKeyboard from 'shell/components/shared/input-keyboard';
import ListGender from 'shell/components/shared/gender';
import PmiTooltipError from 'shell/components/shared/tooltip-error';
import { SearchApplicantHistorical } from 'shell/models/search-accountholder-historical';
import { useUserProfile } from 'shell/hooks/use-user-profile';
import ErrorModal from 'shell/components/shared/anomalie-modal';

export const headCells: HeadCell<PersonVM, keyof PersonVM>[] = [
  {
    id: 'fiscalCode',
    label: 'Codice Fiscale',
  },
  {
    id: 'surname',
    label: 'Cognome',
  },
  {
    id: 'name',
    label: 'Nome',
  },
  {
    id: 'gender',
    label: 'Sesso',
  },
  {
    id: 'birthday',
    label: 'Data di nascita',
  },
  {
    id: 'birthplace',
    label: 'Luogo di nascita',
  },
  {
    id: 'residence',
    label: 'Residenza',
  },
  {
    id: 'state',
    label: 'Stato',
  },
  {
    id: 'anpr',
    label: 'ANPR',
  },
  {
    id: 'action',
    label: 'Azione',
  },
];

interface AccordionGeneralityFormProps {
  disabled?: boolean;
  values?: AccordionGeneralityFormVM;
  applicant?: AccordionApplicantFormVM;
  errors?: FormikErrors<AccordionGeneralityFormVM>;
  touched?: FormikTouched<AccordionGeneralityFormVM>;
  hasAccountholder?: boolean;
  onChange: (
    key: keyof AccordionGeneralityFormVM,
    value: AccordionGeneralityFormVM[keyof AccordionGeneralityFormVM]
  ) => void;
  onChangeValues: (values: AccordionGeneralityFormVM) => void;
  setValues: (values: CertificateFormVM) => void;
  allValues: CertificateFormVM;
  visibilityTable: boolean;
  setVisibilityTable: (visibility: boolean) => void;
  valuesPerson?: AccordionApplicantFormVM;
  errorsPerson?: FormikErrors<AccordionApplicantFormVM>;
  touchedPerson?: FormikTouched<AccordionApplicantFormVM>;
  onChangePerson: (
    key: keyof AccordionApplicantFormVM,
    value: AccordionApplicantFormVM[keyof AccordionApplicantFormVM]
  ) => void;
}

const AccordionGeneralityFormComponent = ({
  disabled,
  values,
  applicant,
  onChange,
  onChangeValues,
  setValues,
  allValues,
  errors,
  touched,
  setVisibilityTable,
  valuesPerson,
  errorsPerson,
  touchedPerson,
  onChangePerson,
}: AccordionGeneralityFormProps): React.ReactElement => {
  const { userProfile } = useUserProfile();

  const [openErrorModal, setOpenErrorModal] = useState(false);

  // Handle choice date
  const handleChooseDateChange = useEventCallback(
    (date: DateTime | null, choice?: Array<DatePickerView>) => {
      onChangeValues({ birthDate: date, birthChoiceDate: choice });
    }
  );

  const [, getAnpr] = useHttpFetch<HttpResponse<SearchApplicantHistorical>, CertificateFormVM>(
    `${__PERSON_API_URL__}/persona/ricerca-per-certificato-storico`,
    {
      onBootstrap: false,
      options: {
        errorCatching: true,
        method: 'POST',
        loading: true,
        data: {},
      },
      transformer: transformerPersonGeneralityList,
    }
  );

  const showTooltip = useMemo(() => {
    if (valuesPerson?.fiscalCode || (valuesPerson?.name && valuesPerson?.surname)) {
      if (errors?.searchApplicant) {
        return true;
      }
      return false;
    }

    if (!valuesPerson?.fiscalCode || (!valuesPerson?.name && !valuesPerson?.surname)) {
      return true;
    }

    return false;
  }, [valuesPerson, errors?.searchApplicant]);

  const tooltipMessage = useMemo(() => {
    if (valuesPerson?.fiscalCode || (valuesPerson?.name && valuesPerson?.surname)) {
      if (errors?.searchApplicant) {
        return errors?.searchApplicant;
      }
      return;
    }
    if (!valuesPerson?.fiscalCode || (!valuesPerson?.name && !valuesPerson?.surname)) {
      return 'Inserire codice fiscale o cognome e nome';
    }
  }, [valuesPerson, errors?.searchApplicant]);

  const handleSearch = useEventCallback(() => {
    setVisibilityTable(false);

    if (!applicant?.fiscalCode && !(applicant?.name && applicant?.surname)) {
      return;
    }

    setValues({
      accountholder: allValues.accountholder,
      applicant: {
        fiscalCode: allValues.applicant?.fiscalCode,
        surname: allValues.applicant?.surname,
        name: allValues.applicant?.name,
        gender: allValues.applicant?.gender,
        withoutName: allValues.applicant?.withoutName,
        withoutSurname: allValues.applicant?.withoutSurname,
        birthDate: allValues.applicant?.birthDate,
        birthChoiceDate: allValues.applicant?.birthChoiceDate,
      },
      generality: {
        birthDate: allValues.generality?.birthDate,
        birthChoiceDate: allValues.generality?.birthChoiceDate,
        isForeign: allValues.generality?.isForeign,
        foreignStateBirth: allValues.generality?.foreignStateBirth,
        provinceOfBirth: allValues.generality?.provinceOfBirth,
        foreignMunicipality: allValues.generality?.foreignMunicipality,
        municipalityOfBirth: allValues.generality?.municipalityOfBirth,
        locality: allValues.generality?.locality,
        numberDocument: allValues.generality?.numberDocument,
        dateReleaseDocument: allValues.generality?.dateReleaseDocument,
        isForeignDocument: allValues.generality?.isForeignDocument,
        consulateCode: allValues.generality?.consulateCode,
        municipalityDocument: allValues.generality?.municipalityDocument,
        residenceString: allValues.generality?.residenceString,
      },
    });

    const data = transformSearchPersonDataset({
      page: 0,
      rowsPerPage: 0,
      orders: {},
      subjectData: {
        fiscalCode: applicant?.fiscalCode,
        name: applicant?.name,
        surname: applicant?.surname,
        withoutName: applicant?.withoutName,
        withoutSurname: applicant?.withoutSurname,
        gender: applicant?.gender,
        birthday: values?.birthDate,
        birthdayChoice: values?.birthChoiceDate,
        isForeign: values?.isForeign,
        foreignMunicipality: values?.foreignMunicipality,
        foreignStateBirth: values?.foreignStateBirth,
        municipalityOfBirth: values?.municipalityOfBirth,
        provinceOfBirth: values?.municipalityOfBirth?.province || undefined,
        // se feature auth anpr === 2
        isNationalResearch: userProfile?.datiLogin?.profile?.feature?.anpr === '2' ? true : false,
        municipalityOfResidence:
          userProfile?.datiLogin?.profile?.feature?.anpr === '1'
            ? {
                id: userProfile?.datiLogin?.codiceIstat || '',
                description: userProfile?.datiLogin?.nomeComune || '',
                province: userProfile?.datiLogin?.siglaProvincia || '',
              }
            : undefined,
      },
      identityCard: {
        number: values?.numberDocument,
        // TODO data rilascio
      },
    });

    (
      getAnpr({
        data,
      }) as ResponsePromise<HttpResponse<SearchApplicantHistorical>, CertificateFormVM>
    )
      .then((response) => {
        const castedResponse = response as CertificateFormVM;

        setValues({
          ...allValues,
          certifiableApplicant: castedResponse.certifiableApplicant,
          anomaly: castedResponse.anomaly,
          isAire: castedResponse.isAire,
          applicant: castedResponse.applicant,
          generality: { ...castedResponse.generality, searchApplicant: true, isFromAnpr: true },
        });
        window.scrollTo(0, 0);
        return;
      })
      .catch(() => {
        setValues({
          ...allValues,
          generality: { ...allValues.generality, searchApplicant: true },
          accountholder: { ...allValues.accountholder, sameApplicant: false },
        });
      });
  });

  const handleSurname = useEventCallback((value: string) => {
    onChangePerson('surname', value);
  });

  // Handle name
  const handleName = useEventCallback((value: string) => {
    onChangePerson('name', value);
  });

  // Handle gender
  const handleGender = useEventCallback((event: SelectChangeEvent<string>) => {
    onChangePerson('gender', event.target.value);
  });

  return (
    <Fragment>
      <Grid container spacing={1}>
        <Grid item xs={12} md={6}>
          <PmiTextField
            label="Codice fiscale *"
            aria-label="Codice fiscale"
            id="fiscal-code"
            fullWidth
            onChange={(event) => {
              onChangePerson('fiscalCode', event?.target.value);
            }}
            value={valuesPerson?.fiscalCode?.toUpperCase() || ''}
            inputProps={{ maxLength: '16' }}
            disabled={disabled}
            error={touchedPerson?.fiscalCode && Boolean(errorsPerson?.fiscalCode)}
            helperText={errorsPerson?.fiscalCode}
          />
        </Grid>
        <Grid
          item
          container
          spacing={0}
          columnSpacing={{ xs: 0, md: 1 }}
          xs={12}
          md={10}
          alignItems="center"
        >
          <Grid item xs={12} md={9}>
            <InputKeyboard
              label="Cognome *"
              id="surname"
              aria-label="Cognome"
              onChange={handleSurname}
              disabled={disabled}
              value={valuesPerson?.surname || ''}
              touched={touchedPerson?.surname}
              error={errorsPerson?.surname}
            />
          </Grid>
          <Grid item xs={12} md={3}>
            <FormControlLabel
              control={
                <Checkbox
                  id="withoutSurname"
                  checked={valuesPerson?.withoutSurname || false}
                  onChange={() => onChangePerson('withoutSurname', !valuesPerson?.withoutSurname)}
                  inputProps={{ 'aria-label': 'Persona senza cognome' }}
                  disabled={disabled}
                />
              }
              label="Persona senza cognome"
            />
          </Grid>
        </Grid>
        <Grid
          item
          container
          spacing={0}
          columnSpacing={{ xs: 0, md: 1 }}
          xs={12}
          md={10}
          alignItems="center"
        >
          <Grid item xs={12} md={9}>
            <InputKeyboard
              label="Nome *"
              aria-label="Nome"
              id="name"
              disabled={disabled}
              onChange={handleName}
              value={valuesPerson?.name || ''}
              touched={touchedPerson?.name}
              error={errorsPerson?.name}
            />
          </Grid>
          <Grid item xs={12} md={3}>
            <FormControlLabel
              control={
                <Checkbox
                  id="withoutName"
                  disabled={disabled}
                  checked={valuesPerson?.withoutName || false}
                  onChange={() => onChangePerson('withoutName', !valuesPerson?.withoutName)}
                  inputProps={{ 'aria-label': 'Persona senza nome' }}
                />
              }
              label="Persona senza nome"
            />
          </Grid>
        </Grid>
        <Grid item xs={12} md={5}>
          <ListGender
            required
            value={valuesPerson?.gender || ''}
            onChange={handleGender}
            error={touchedPerson?.gender && Boolean(errorsPerson?.gender)}
          />
        </Grid>
      </Grid>
      <Grid container spacing={0} columnSpacing={{ xs: 0, md: 1 }} rowSpacing={1}>
        <Grid item xs={12} container spacing={0} alignItems="center">
          <ChooseDate
            required
            selected={values?.birthDate || null}
            onChange={handleChooseDateChange}
            choice={values?.birthChoiceDate}
            disabled={disabled}
            label="Data nascita"
            error={touched?.birthDate && Boolean(errors?.birthDate)}
          />
          <ItalianForeignState
            isForeign={values?.isForeign}
            foreignStateBirth={values?.foreignStateBirth}
            foreignMunicipality={values?.foreignMunicipality}
            provinceOfBirth={values?.provinceOfBirth}
            municipalityOfBirth={values?.municipalityOfBirth}
            locality={values?.locality}
            values={values}
            onChange={onChange}
            onChangeValues={onChangeValues || noop}
            errors={errors}
            touched={touched}
          />
          <Document
            onChange={onChange}
            values={values}
            errors={errors}
            touched={touched}
            onChangeValues={onChangeValues || noop}
          />
          <Grid container spacing={2} item xs={12} display="flex" justifyContent="flex-end">
            <Grid item xs={12} md={4}>
              <PmiTooltipError show={showTooltip} message={tooltipMessage}>
                <span>
                  <PmiButton
                    color="cdmBrandThird"
                    variant="outlined"
                    onClick={handleSearch}
                    fullWidth
                    id="handle-search-anpr"
                  >
                    Cerca
                  </PmiButton>
                </span>
              </PmiTooltipError>
            </Grid>
          </Grid>
        </Grid>
      </Grid>
      <ErrorModal
        open={openErrorModal}
        onClose={() => {
          setOpenErrorModal(false);
        }}
        dataPersons={allValues.anomaly}
      />
    </Fragment>
  );
};

AccordionGeneralityFormComponent.displayName = 'Accordion generality applicant form component';

const AccordionGeneralityForm = memo(AccordionGeneralityFormComponent);

export default AccordionGeneralityForm;
